import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import U2000DetailTable from "../../../../../u2000Core/components/tables/detail/U2000DetailTable";
import U2000ActionBtn from "../../../../../u2000Core/components/buttons/U2000ActionBtn";
import { FaPlus, FaTrashAlt } from "react-icons/fa";
import U2000ResponsiveToolbar from "../../../../../u2000Core/components/toolbar/U2000ResponsiveToolbar";
import { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { u2000IntFormState, useU2000FormStateHandlers, type U2000FormSBusinessHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import u2000FormState from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import { U2000FormStateEnumState } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import { U2000IdColumnDef, type U2000TableColumn } from "../../../../../u2000Core/components/tables/basic/U2000BasicTableList";
import { U2000IdCheckedBoxDisplay } from "../../../../../u2000Core/components/tables/display/U2000IdCheckedBoxDisplay";
import { useU2000AuthContext } from "../../../../../u2000Core/context/auth/U2000UseAuthContext";
import type { U3121Employee, U3121SubProject, U3121TimesheetAccessWithSource } from "../../../../U3121Service";
import { NS, U3121ROLES } from "../../../../U3121";
import { u3121SubProjectTimesheetAccessHandler } from "./U3121SubProjectTimesheetAccessHandlers";
import U3121SubProjectTimesheetAccessEditDlg from "./U3121SubProjectTimesheetAccessEditDlg";
import U3121SubProjectTimesheetAccessDeleteDlg from "./U3121SubProjectTimesheetAccessDeleteDlg";

/** Aplatit un item renvoyé par l'API (forme { employee: {...} } ou déjà plat { id, firstName, ... }). */
function normalizeTimesheetAccessItem(item: U3121Employee | { employee?: U3121Employee; id?: string }): U3121Employee {
  if (!item) return { id: "", firstName: "", lastName: "", codeEmployee: "" };
  const emp = (item as { employee?: U3121Employee }).employee;
  if (emp && (emp.id || emp.firstName || emp.lastName || emp.codeEmployee)) {
    return { id: emp.id ?? "", firstName: emp.firstName, lastName: emp.lastName, codeEmployee: emp.codeEmployee };
  }
  return item as U3121Employee;
}

const SUBPROJECT_ID_PREFIX = "subproject_";
const PROJECT_ID_PREFIX = "project_";

function displayEmployeeLabel(row: U3121Employee): string {
  if (!row) return "";
  const firstName = row.firstName ?? "";
  const lastName = row.lastName ?? "";
  const code = row.codeEmployee ?? "";
  const name = [firstName, lastName].filter(Boolean).join(" ").trim();
  return code ? `${name} (${code})` : name || row.id || "";
}

function getColumnsDef(t: TFunction, language: string): U2000TableColumn[] {
  return [
    {
      ...U2000IdColumnDef,
      display: (column: U2000TableColumn, value: unknown, row: U3121TimesheetAccessWithSource, allowRowSelection: boolean, rowUpdated?: (r: unknown) => void) => {
        const isDisabled = row?.source === "project";
        const content = (
          <U2000IdCheckedBoxDisplay
            column={column}
            value={value}
            row={(row ?? {}) as unknown as Record<string, unknown>}
            disabled={isDisabled}
            rowUpdated={rowUpdated}
            allowRowSelection={allowRowSelection}
          />
        );
        return isDisabled ? (
          <span title={t("msgEmployeeAssignmentRecordNotModifiableSubProject", { ns: NS })} className="inline-flex items-center justify-center w-full">
            {content}
          </span>
        ) : (
          content
        );
      },
    },
    {
      name: "source",
      label: t("lblSource", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[18%]",
      type: "string",
      align: "left",
      display: (_column: U2000TableColumn, _value: unknown, row: U3121TimesheetAccessWithSource) => {
        if (!row) return "";
        const lang = language;
        if (row.sourceLabelFr != null || row.sourceLabelEn != null) return lang === "en" ? (row.sourceLabelEn ?? row.sourceLabelFr ?? "") : (row.sourceLabelFr ?? row.sourceLabelEn ?? "");
        if (row.source === "project") return t("employeeAssignmentSourceProject", { ns: NS });
        if (row.source === "subproject") return t("employeeAssignmentSourceSubproject", { ns: NS });
        return "";
      },
    },
    {
      name: "codeEmployee",
      label: t("lblEmployee", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      isDefaultSortedColumn: true,
      widthClass: "w-[82%]",
      type: "string",
      align: "left",
      display: (_column: U2000TableColumn, _value: unknown, row: U3121TimesheetAccessWithSource) => displayEmployeeLabel(row),
    },
  ];
}

interface U3121SubProjectTimesheetAccessBodyProps {
  formStateHandlers: {
    getData: () => U3121SubProject | null;
    setData: (data: U3121SubProject) => void;
    getFormState?: () => U2000FormStateEnumState;
  };
}

type TimesheetAccessRawItem = U3121Employee | { employee?: U3121Employee; id?: string; source?: string; sourceLabelFr?: string; sourceLabelEn?: string };
type TimesheetAccessWithSourceFields = { source?: string; sourceLabelFr?: string; sourceLabelEn?: string };

/**
 * Construit la liste fusionnée (projet + sous-projet) pour l'onglet Accès feuille de temps sous-projet.
 * Si l'API renvoie déjà sub.timesheetAccess avec un champ source sur chaque item, on préserve cette source.
 * Sinon on fusionne sub.project?.timesheetAccess (source project) et sub.timesheetAccess (source subproject).
 */
function buildMergedTimesheetAccess(sub: U3121SubProject | null): U3121TimesheetAccessWithSource[] {
  if (!sub) return [];
  const list = Array.isArray(sub.timesheetAccess) ? sub.timesheetAccess : [];
  const hasSourceFromApi = list.some((r) => {
    const s = (r as TimesheetAccessWithSourceFields).source;
    return s != null && s !== "";
  });

  if (hasSourceFromApi) {
    return list.map((r) => {
      const raw = r as TimesheetAccessRawItem & TimesheetAccessWithSourceFields;
      const emp = normalizeTimesheetAccessItem(r as U3121Employee | { employee?: U3121Employee });
      const src = (raw.source === "project" || raw.source === "subproject" ? raw.source : "subproject") as "project" | "subproject";
      const prefix = src === "project" ? PROJECT_ID_PREFIX : SUBPROJECT_ID_PREFIX;
      return {
        ...emp,
        id: `${prefix}${emp.id}`,
        source: src,
        sourceLabelFr: raw.sourceLabelFr,
        sourceLabelEn: raw.sourceLabelEn,
      };
    });
  }

  const projectList = Array.isArray(sub.project?.timesheetAccess) ? sub.project.timesheetAccess : [];
  const subList = list;
  const fromProject: U3121TimesheetAccessWithSource[] = projectList.map((r) => {
    const emp = normalizeTimesheetAccessItem(r as U3121Employee | { employee?: U3121Employee });
    return { ...emp, id: `${PROJECT_ID_PREFIX}${emp.id}`, source: "project" as const };
  });
  const fromSub: U3121TimesheetAccessWithSource[] = subList.map((r) => {
    const emp = normalizeTimesheetAccessItem(r as U3121Employee | { employee?: U3121Employee });
    return { ...emp, id: `${SUBPROJECT_ID_PREFIX}${emp.id}`, source: "subproject" as const };
  });
  return [...fromProject, ...fromSub];
}

/**
 * Onglet Accès feuille de temps sous-projet : liste fusionnée (projet + sous-projet), colonne Source, suppression uniquement sur les lignes sous-projet.
 */
const U3121SubProjectTimesheetAccessBody: React.FC<U3121SubProjectTimesheetAccessBodyProps> = ({ formStateHandlers }) => {
  const { t, i18n } = useTranslation();
  const { hasRole } = useU2000AuthContext();
  const subProject = formStateHandlers.getData?.() ?? null;
  const mergedList = useMemo(() => buildMergedTimesheetAccess(subProject), [subProject]);

  const timesheetAccessDataKey = useMemo(() => {
    return `${subProject?.id ?? ""}_${mergedList.length}_${mergedList.map((r) => r.id).filter(Boolean).sort().join(",")}`;
  }, [subProject?.id, mergedList]);

  const [formState, formStateDispatch] = React.useReducer(u2000FormState, u2000IntFormState);
  const gridStateHandlers = useU2000GridStateHandlers<U3121TimesheetAccessWithSource>();

  const businessHandlers: U2000FormSBusinessHandlers<U3121Employee> = useMemo(
    () => ({
      newRecord: () => ({ id: "", firstName: "", lastName: "", codeEmployee: "" }),
      validateForm: (data) => {
        const result: Record<string, string> = {};
        if (!data?.id?.trim()) {
          result["id"] = t("errTimesheetAccessEmployeeRequired", { ns: NS });
        }
        return result;
      },
    }),
    [t],
  );

  const assignmentFormStateHandlers = useU2000FormStateHandlers<U3121Employee>({
    formState,
    formStateDispatch,
    businessHandlers,
  });

  const handler = u3121SubProjectTimesheetAccessHandler({
    gridStateHandlers,
    t,
    formStateHandlers: assignmentFormStateHandlers,
    parentFormHandlers: formStateHandlers,
  });

  useEffect(() => {
    const columns = getColumnsDef(t, i18n.language);
    gridStateHandlers.setColumns(columns);
    gridStateHandlers.setSelectedFields(columns.filter((col) => col.isDefaultColumn).map((col) => col.name));
    gridStateHandlers.setSelectedSortedFields(columns.filter((col) => col.isDefaultSortedColumn).map((col) => ({ name: col.name, sortOrder: "ASC" as const })));
    gridStateHandlers.setData(mergedList);
    gridStateHandlers.setTotalRows(mergedList.length);
    gridStateHandlers.setErrors([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- gridStateHandlers volontairement exclu des deps
  }, [timesheetAccessDataKey, t, i18n.language]);

  useEffect(() => {
    if (handler.dataChangedEvent === 0) return;
    const sub = formStateHandlers.getData();
    if (!sub) return;
    const gridData = gridStateHandlers.getData();
    // Conserver la liste fusionnée (projet + sous-projet) dans le state pour que les lignes "projet" restent visibles.
    // Le filtrage pour n'envoyer que les lignes sous-projet se fait dans subProjectPayloadForSave.
    const mergedForState = gridData.map((r) => ({
      id: r.id,
      firstName: r.firstName,
      lastName: r.lastName,
      codeEmployee: r.codeEmployee,
      source: r.source,
      sourceLabelFr: r.sourceLabelFr,
      sourceLabelEn: r.sourceLabelEn,
    }));
    formStateHandlers.setData({
      ...sub,
      timesheetAccess: mergedForState as unknown as U3121Employee[],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- formStateHandlers/gridStateHandlers exclus pour éviter récursion
  }, [handler.dataChangedEvent]);

  const isEditMode = formStateHandlers.getFormState?.() === U2000FormStateEnumState.EDIT;
  const selectedRows = handler.gridStateHandlers.getSelectedRows();
  const selectedSubprojectRows = selectedRows.filter((r) => r.source === "subproject");
  const disabledDelete = !isEditMode || selectedSubprojectRows.length === 0;

  const actions = useMemo(
    () => [
      {
        key: "add",
        label: t("btnAdd", { ns: NS }),
        handleClick: handler.onAdd,
        hasRole: hasRole(U3121ROLES.EDIT),
        disabled: !isEditMode,
        icon: FaPlus,
        Component: U2000ActionBtn,
      },
      {
        key: "delete",
        label: t("btnDelete", { ns: NS }),
        handleClick: handler.onDelete,
        hasRole: hasRole(U3121ROLES.EDIT),
        disabled: disabledDelete,
        icon: FaTrashAlt,
        Component: U2000ActionBtn,
      },
    ],
    [t, handler, isEditMode, disabledDelete, hasRole],
  );

  return (
    <>
      <U3121SubProjectTimesheetAccessEditDlg handler={handler} t={t} NS={NS} />
      <U3121SubProjectTimesheetAccessDeleteDlg handler={handler} t={t} NS={NS} />

      <div className="mt-1 mb-1 flex items-center gap-x-2 border-b-2 pb-3">
        <U2000ResponsiveToolbar actions={actions} />
      </div>
      <U2000DetailTable
        gridStateHandlers={gridStateHandlers}
        rowClickFn={(row) => handler.onRowClick(row as U3121TimesheetAccessWithSource)}
        allowRowSelection={isEditMode}
        label={t("lblTimesheetAccess", { count: gridStateHandlers.getTotalRows(), ns: NS })}
      />
    </>
  );
};

export default U3121SubProjectTimesheetAccessBody;
